import { z } from 'zod';
import { Email, IsoDateTime, LocalDate, TimeOfDay, Uuid, withDefaults } from './common.js';
/** Module `reports` (docs/modules/reports.md). Owner: task S17. */
export const ExportFormat = z.enum(['csv', 'xlsx']);
export const HistoryQuery = z.object({
    siteId: Uuid.optional(),
    groupId: Uuid.optional(),
    memberId: Uuid.optional(),
    /** Site-local dates, inclusive; max 366 days. */
    from: LocalDate,
    to: LocalDate,
    search: z.string().trim().max(120).optional(),
    status: z.enum(['all', 'open', 'closed']).default('all'),
    sort: z.enum(['signedInAt', 'name', 'site', 'group']).default('signedInAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export const HistoryRow = z.object({
    visitId: Uuid,
    name: z.string(),
    siteId: Uuid,
    siteName: z.string(),
    groupId: Uuid,
    groupName: z.string(),
    hostName: z.string().nullable(),
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime.nullable(),
    durationMinutes: z.number().int().nullable(),
    signInMethod: z.string(),
    signOutMethod: z.string().nullable(),
    flags: z.array(z.string()),
    protected: z.boolean(),
    timeZone: z.string(),
});
export const HistoryPage = z.object({ items: z.array(HistoryRow), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });
export const HistoryExportQuery = HistoryQuery.omit({ page: true, pageSize: true }).extend({ format: ExportFormat });
/** Timesheet: a group's sign-ins across sites, per person per day. */
export const TimesheetQuery = z.object({
    groupId: Uuid,
    siteId: Uuid.optional(),
    from: LocalDate,
    to: LocalDate,
    memberId: Uuid.optional(),
});
export const TimesheetSegment = z.object({
    visitId: Uuid,
    siteId: Uuid,
    siteName: z.string(),
    /** Minutes from local midnight of the day (0-1440), clipped to the day. */
    startMinute: z.number().int(),
    endMinute: z.number().int(),
    open: z.boolean(),
    edited: z.boolean(),
    autoSignedOut: z.boolean(),
});
export const TimesheetDay = z.object({ date: LocalDate, minutes: z.number().int(), segments: z.array(TimesheetSegment) });
export const TimesheetPerson = z.object({
    memberId: Uuid.nullable(),
    name: z.string(),
    totalMinutes: z.number().int(),
    days: z.array(TimesheetDay),
});
export const Timesheet = z.object({ groupId: Uuid, from: LocalDate, to: LocalDate, timeZone: z.string(), people: z.array(TimesheetPerson) });
export const TimesheetExportQuery = TimesheetQuery.extend({ format: ExportFormat });
/** Add a record (timesheets.edit). Times must satisfy in < out <= now. */
export const TimesheetEntryCreate = z.object({
    memberId: Uuid,
    siteId: Uuid,
    signedInAt: IsoDateTime,
    signedOutAt: IsoDateTime,
    reason: z.string().trim().min(1).max(500),
});
export const TimesheetEntryUpdate = z.object({
    signedInAt: IsoDateTime.optional(),
    signedOutAt: IsoDateTime.nullable().optional(),
    reason: z.string().trim().min(1).max(500),
});
export const TimesheetEntryDelete = z.object({ reason: z.string().trim().min(1).max(500) });
/** The audit trail of one record: every sign-in/out/edit event, with before/after for edits. */
export const TimesheetEntryHistory = z.object({
    visitId: Uuid,
    events: z.array(z.object({
        kind: z.enum(['sign_in', 'sign_out', 'edit', 'delete']),
        at: IsoDateTime,
        actorLabel: z.string().nullable(),
        method: z.string().nullable(),
        before: z.object({ signedInAt: IsoDateTime.nullable(), signedOutAt: IsoDateTime.nullable() }).nullable(),
        after: z.object({ signedInAt: IsoDateTime.nullable(), signedOutAt: IsoDateTime.nullable() }).nullable(),
        reason: z.string().nullable(),
        applied: z.boolean(),
    })),
});
/** Site settings: who is still on site at a set time each day, emailed (spec 2.2). */
export const ReportsSiteSettings = z.object({
    onSiteReport: withDefaults(z.object({
        enabled: z.boolean().default(false),
        time: TimeOfDay.default('17:30'),
        recipients: z.array(Email).max(20).default([]),
        /** Empty = every group at the site. */
        groupIds: z.array(Uuid).max(200).default([]),
        weekdaysOnly: z.boolean().default(true),
        /** Send even when nobody is on site. */
        sendWhenEmpty: z.boolean().default(false),
    })),
});
export const ReportsTenantSettings = z.object({
    /** Largest export (rows). */
    maxExportRows: z.number().int().min(1000).max(500000).default(100000),
});
/** POST /reports/on-site/:siteId/send-now (sites.manage): send today's report now, for checking the settings. */
export const OnSiteReportSendResult = z.object({ recipients: z.number().int(), people: z.number().int() });
//# sourceMappingURL=reports.js.map